// 인생 진행 엔진 (순수 함수, 서버·화면과 무관). 상태는 JSON 으로 저장 가능.
import { luckAt } from './luckPhase.js';
import { classifyTransition, getTransitionTable } from './luckTransition.js';
import { essenceOf } from './essence.js';
import { directionFor } from './arcDirection.js';
import { luckScene } from './luckScene.js';
import { rankByFit, jobFit } from './jobFit.js';
import { JOB_CHANGE_RULES, changeWindow, levelAfterChange } from './jobChange.js';
import { mulberry32 } from './rng.js';
import { prepareEvent, getEventHistory, choiceEffects, OUTCOME_LABEL } from './events.js';
import { LIFE_RULES, timePlan, lifePhase, preparationFor, personAvailability, referenceFingerprint } from './life.js';
import { GROUPS, GROUP_LABEL, EL_TRAIT, TRAIT_EL, JOB_RULES, STAT_MIN, STAT_MAX, OPEN_THRESHOLD, BALANCE_RULES, AUTO_CLIMATE_WEIGHT, AUTO_RULES } from './rules.js';
import { climateOf, climateTilt } from './profile.js';
import { initEconomy, stepEconomy } from './economy.js';
import { SCENE_MODEL, applySceneMoney, applySceneLife } from './scenes.js';
import { meetsEducation } from './education.js';
import { LIFE_MODEL, initLife, rollLife, applyLifeAct, lifeEventFor, lifeRecordText } from './lifeEvents.js';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const roundStat = (v) => Math.round((v + Number.EPSILON) * 10) / 10;
const signed = (v) => `${v >= 0 ? '+' : ''}${v}`;
const STEM_EL = [0, 0, 1, 1, 2, 2, 3, 3, 4, 4];
const BRANCH_MAIN = [9, 5, 0, 1, 4, 2, 3, 5, 6, 7, 4, 8];

/** 일간 기준 그 천간이 속한 십성 그룹 */
export const groupOfStem = (dayStem, stem) => GROUPS[(STEM_EL[stem] - STEM_EL[dayStem] + 5) % 5];
const yearRng = (s, age, salt) => mulberry32((s.seed + age * 7919 + salt * 104729) >>> 0);

export function createRun({ seed, birth, saju }) {
  const birthYear = +saju.solarDate.slice(0, 4);
  const v = Object.values(saju.elements);
  const balance = 1 - (Math.max(...v) - Math.min(...v)) / 100; // 오행이 고를수록 1에 가까움
  const rng = mulberry32((seed ^ 0x9e3779b9) >>> 0);
  const deathAge = 68 + Math.floor(rng() * 14) + Math.round(balance * 10); // 숨김값
  const run = {
    seed, birth, saju, birthYear, age: 0, status: 'play', deathAge, balanceVersion: BALANCE_RULES.version,
    // 운은 스탯을 직접 올리지 않는 환경이다(규칙 버전 6). 이 값이 없는 옛 저장은 옛 방식(운 가산)을 그대로 쓴다.
    luckModel: 'environment',
    // 인생 사건(결혼·출산·주택 등)은 이 값이 있는 새 진행에만 켜진다. 옛 저장은 켜지 않는다.
    // 새 형식 장면(v2)은 이 값이 있는 새 진행에만 쓴다. 옛 저장은 옛 이벤트를 그대로 쓴다.
    sceneModel: SCENE_MODEL,
    lifeModel: LIFE_MODEL, life: { ...initLife(), economy: initEconomy() },
    engineRulesVersion: LIFE_RULES.version, initialStatsVersion: saju.statBasis?.version ?? null,
    referenceData: null, yearCommitment: null, relationshipAge: -1, decisionTrace: [],
    stats: { ...saju.stats },
    momentum: Object.fromEntries(GROUPS.map((g) => [g, 1])),
    job: null, jobDeclined: false, branchPending: false, jobHistory: [], jobChangeUsedFor: null,
    seen: [], eventLastSeen: {}, eventHistory: [], log: [], history: [], people: [], meetAge: -1,
  };
  run.people = childhoodFriends(run); // 어릴 때부터 알던 친구 2명으로 시작한다
  return run;
}

export function attachReferenceData(prev, { events, jobs }) {
  if (prev.referenceData) return prev;
  const ordered = { events: [...events].sort((a, b) => a.id.localeCompare(b.id)),
    jobs: [...jobs].sort((a, b) => a.id.localeCompare(b.id)) };
  return { ...prev, referenceData: { ...structuredClone(ordered), fingerprint: referenceFingerprint(ordered),
    version: Math.max(0, ...events.map((event) => event.contentVersion ?? 0)) } };
}

/**
 * 새 형식 장면(client 데이터) 진행의 기준 데이터 사본이 지금 장면 데이터와 다르면 최신으로 바꾼다.
 * 장면 데이터는 서버가 아니라 클라이언트에 있어서, 오류를 고친 뒤에도 옛 저장이 옛 사본으로 진행하는 일을 막는다. 진행 상태(능력치·기록)는 건드리지 않는다.
 */
export function refreshSceneData(s, scenes) {
  if (!s.sceneModel || !s.referenceData) return s;
  const ordered = { events: [...scenes].sort((x, y) => x.id.localeCompare(y.id)), jobs: s.referenceData.jobs };
  if (referenceFingerprint(ordered) === s.referenceData.fingerprint) return s;
  return attachReferenceData({ ...s, referenceData: null }, { events: scenes, jobs: s.referenceData.jobs });
}

/** 그 나이의 기회: {map: 그룹→크기, open: 열린 그룹 배열} */
export function opensFor(s, age) {
  const ds = s.saju.day.stem, year = s.birthYear + age, map = Object.fromEntries(GROUPS.map((g) => [g, 0]));
  const seunStem = (((year - 4) % 10) + 10) % 10, seunBranch = (((year - 4) % 12) + 12) % 12;
  map[groupOfStem(ds, seunStem)] += 10;
  map[groupOfStem(ds, BRANCH_MAIN[seunBranch])] += 5;
  const dw = [...s.saju.daewoon.list].reverse().find((d) => d.age <= age);
  if (dw) {
    map[groupOfStem(ds, dw.stem)] += 5;
    map[groupOfStem(ds, BRANCH_MAIN[dw.branch])] += 3;
  }
  return { map, open: age >= JOB_RULES.eventStartAge ? GROUPS.filter((g) => map[g] >= OPEN_THRESHOLD) : [] };
}

// 기질 점수: 오행 비율. 일간 자신의 오행은 대체로 가장 크게 나와 자동 선택을 한쪽으로 끌어가므로 비중을 줄인 뒤 합이 100이 되게 맞춘다.
// 0.5는 400개 원국 시뮬레이션으로 정한 게임 보정값이다(고서 근거 없음). 0.3은 해당 오행 분야가 거의 선택되지 않아 쓰지 않는다.
export const OWN_ELEMENT_WEIGHT = 0.5;
export const traitScore = (s, trait) => {
  const el = s.saju.elements, own = ['목', '화', '토', '금', '수'][STEM_EL[s.saju.day?.stem ?? 0]], key = TRAIT_EL[trait];
  const adj = Object.fromEntries(Object.keys(el).map((k) => [k, k === own ? el[k] * OWN_ELEMENT_WEIGHT : el[k]]));
  const sum = Object.values(adj).reduce((a, b) => a + b, 0) || 1;
  return (adj[key] ?? 0) * 100 / sum;
};
export const jobTitle = (j) => (!j ? null : j.level <= 3 ? j.levels[j.level - 1] : j.branch?.name ?? j.levels[2]);

// 첫 직업은 24세부터이고, 군 복무 같은 장면 선택으로 늦어질 수 있다(life.jobDelay, 년)
export const needsJob = (s) => !s.job && !s.jobDeclined && s.age >= JOB_RULES.firstJobAge + (s.life?.jobDelay ?? 0) && s.age < JOB_RULES.retireAge;

/**
 * 첫 직업 후보 3개: 스탯이 높은 분야 순으로 분야마다 1개 (jobs: 서버 직업 목록).
 * 새 규칙(luckModel: 'environment')에서는 분야 안에서 서사 축 방향과 가장 어울리는 직업을 고르고(동점은 시드 난수),
 * 방향 분야가 상위 3개에 없으면 마지막 제안을 방향 분야 직업으로 바꾼다. 옛 저장은 이전 방식 그대로다.
 */
export function jobOffers(s, allJobs) {
  const jobs = (allJobs ?? []).filter((job) => meetsEducation(s, job)); // 학력이 안 되는 직업은 제안하지 않는다
  const rng = yearRng(s, s.age, 5);
  const fields = [...GROUPS].sort((a, b) => s.stats[b] - s.stats[a]).slice(0, 3);
  const direction = s.luckModel === 'environment' ? directionFor(essenceOf(s.saju), s.age) : null;
  if (!direction) {
    return fields.map((f) => {
      const pool = jobs.filter((j) => j.field === f);
      return pool[Math.floor(rng() * pool.length)];
    }).filter(Boolean);
  }
  const bestOf = (pool) => {
    if (!pool.length) return null;
    const ranked = rankByFit(pool, direction), top = ranked[0].fit;
    const tied = ranked.filter((r) => r.fit === top);
    return tied[Math.floor(rng() * tied.length)];
  };
  const reason = (fit) => (fit < 2 ? '' : direction.theme ? `이 시기의 주제: ${direction.theme}` : '원국이 가리키는 분야와 맞는 직업');
  const picks = fields.map((f) => bestOf(jobs.filter((j) => j.field === f))).filter(Boolean);
  if (picks.length === 3 && !fields.some((f) => direction.fields.includes(f))) {
    const replacement = bestOf(jobs.filter((j) => direction.fields.includes(j.field)));
    if (replacement) picks[2] = replacement;
  }
  return picks.map(({ job, fit }) => ({ ...job, fit, reason: reason(fit) }));
}

export function chooseJob(prev, job) {
  const s = structuredClone(prev);
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'job', jobId: job?.id ?? null, label: job?.name ?? '이번 제안 거절' });
  if (!job) { s.jobDeclined = true; s.log.unshift({ age: s.age, kind: 'job', text: '첫 직업 제안은 받지 않았다.' }); return s; }
  s.job = { id: job.id, name: job.name, field: job.field, sub: job.sub, levels: job.levels, branches: job.branches, level: 1, branch: null };
  s.log.unshift({ age: s.age, text: `${job.name} 일을 시작했다 (${job.levels[0]})`, kind: 'job' });
  return s;
}
// ───────── 전직 ─────────
const seededShuffle = (list, rng) => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) { const j = Math.floor(rng() * (i + 1)); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
};

/**
 * 전직 제안. 열려 있지 않으면 null.
 * 현재 직업보다 서사 축 방향과 더 어울리는 직업만 후보가 되며, 어울림이 높은 순(같은 어울림은 시드 난수)으로 최대 3개.
 */
export function jobChangeOffer(s, jobs) {
  const window = changeWindow(s);
  if (!window) return null;
  const { luck, direction } = window;
  const currentFit = jobFit(s.job, direction);
  const candidates = (jobs ?? []).filter((job) => job.id !== s.job.id && meetsEducation(s, job))
    .map((job) => ({ job, fit: jobFit(job, direction) })).filter((c) => c.fit > currentFit);
  if (!candidates.length) return null;
  const rng = yearRng(s, s.age, 6);
  const tiers = [...new Set(candidates.map((c) => c.fit))].sort((a, b) => b - a);
  const ordered = tiers.flatMap((fit) => seededShuffle(candidates.filter((c) => c.fit === fit), rng));
  const reason = direction.theme ? `이 시기의 주제: ${direction.theme}` : '원국이 가리키는 분야와 맞는 직업';
  return {
    age: s.age, currentFit, luck: { ten: luck.ten, startAge: luck.startAge },
    offers: ordered.slice(0, JOB_CHANGE_RULES.maxOffers).map(({ job, fit }) => ({ ...job, fit, reason })),
  };
}

/** 자동 진행용 전직: 제안 중 기질·능력치·어울림 점수가 가장 높은 직업. 현재보다 어울리지 않으면 null(유지). */
export function autoJobChange(s, offer) {
  const pick = autoJob(s, offer?.offers);
  if (!pick || !(pick.job.fit > (offer.currentFit ?? 0))) return null;
  return { job: pick.job, reason: `전직: ${pick.reason}` };
}

/**
 * 인생 사건(직업 전환)에서 새 일을 찾기로 한 뒤 나오는 직업 후보. 같은 분야('same')나 다른 분야('other')에서 최대 3개.
 * 학력이 안 되는 직업은 빠지고, 후보가 없으면 null 이다. 전직 모달과 changeJob 을 그대로 쓸 수 있는 모양이다.
 */
export function pendingJobOffer(s, jobs) {
  const kind = s.pendingJobSwitch;
  if (!kind || !s.job) return null;
  const pool = (jobs ?? []).filter((job) => job.id !== s.job.id && meetsEducation(s, job));
  const first = pool.filter((job) => (kind === 'same') === (job.field === s.job.field));
  const list = first.length ? first : pool;
  if (!list.length) return null;
  const picked = seededShuffle(list, yearRng(s, s.age, 232)).slice(0, 3);
  return { age: s.age, currentFit: 0, luck: { ten: null, startAge: null }, pending: kind,
    offers: picked.map((job) => ({ ...job, fit: 0, reason: kind === 'same' ? '같은 분야의 다른 자리' : '다른 분야의 일' })) };
}

export function changeJob(prev, job, offer) {
  const s = structuredClone(prev);
  const from = s.job, level = levelAfterChange(from.level);
  s.jobHistory = [...(s.jobHistory ?? []), { age: s.age, from: { id: from.id, name: from.name, level: from.level },
    to: { id: job.id, name: job.name, level }, ten: offer.luck?.ten ?? null, fit: job.fit ?? null }];
  if (offer.luck?.startAge != null) s.jobChangeUsedFor = offer.luck.startAge;
  s.pendingJobSwitch = null;
  s.job = { id: job.id, name: job.name, field: job.field, sub: job.sub, levels: job.levels, branches: job.branches,
    level, branch: null, since: s.age };
  s.branchPending = false;
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'jobChange', jobId: job.id, label: job.name });
  s.log.unshift({ age: s.age, kind: 'job', text: `${from.name} 일을 그만두고 ${job.name} 일을 시작했다 (${job.levels[level - 1]})` });
  return s;
}

export function declineJobChange(prev, offer) {
  const s = structuredClone(prev);
  if (offer.luck?.startAge != null) s.jobChangeUsedFor = offer.luck.startAge;
  s.pendingJobSwitch = null;
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'jobChange', jobId: null, label: '전직 제안 거절' });
  s.log.unshift({ age: s.age, kind: 'job', text: '전직 제안은 받지 않았다.' });
  return s;
}

export function chooseBranch(prev, idx) {
  const s = structuredClone(prev);
  s.job.branch = s.job.branches[idx]; s.branchPending = false;
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'branch', branchIdx: idx, label: s.job.branch.name });
  s.log.unshift({ age: s.age, text: `${s.job.branch.name}의 길을 택했다`, kind: 'job' });
  return s;
}
/** 4단계 갈래: 기질 점수가 높은 쪽을 앞에 */
export function branchOrder(s) {
  return s.job.branches.map((b, i) => ({ ...b, idx: i, score: traitScore(s, b.trait) })).sort((a, b) => b.score - a.score);
}

/** 원국은 사건 노출만 보정한다. 초기 점수를 현재 스탯과 이중으로 곱하지 않는다. */
export function eventWeightParts(s, event, focus) {
  const field = event.field;
  const profile = s.saju.statBasis?.eventAffinity;
  const hasNatalProfile = Number.isFinite(profile?.[field]);
  const natal = hasNatalProfile
    ? clamp(profile[field], BALANCE_RULES.natalEventMin, BALANCE_RULES.natalEventMax) : 1;
  const current = s.stats[field] ?? STAT_MIN;
  const initial = s.saju.stats[field] ?? current;
  // 새 원국 프로필에서는 플레이로 쌓은 변화만 추가 반영. 옛 진행의 추첨식은 유지한다.
  const practice = 1 + (hasNatalProfile ? Math.max(0, current - initial) : current) / 100;
  const focused = focus === field ? 2 : 1;
  const opened = opensFor(s, s.age).open.includes(field) ? 1.5 : 1;
  return { base: event.baseWeight, natal, practice, focused, opened,
    total: event.baseWeight * natal * practice * focused * opened };
}

/** 올해 나올 이벤트 (없으면 null). candidates: 서버에서 받은 해당 나이 후보 */
export function drawEvent(s, candidates, focus, focusUnits = focus ? 2 : 0) {
  if (s.age < JOB_RULES.eventStartAge || !candidates?.length) return null;
  const activeFocus = focusUnits > 0 ? focus : null;
  const eligible = candidates.map((e) => prepareEvent(s, e, BALANCE_RULES.defaultEventCooldownYears))
    .filter(Boolean).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  // 진행 중인 이야기의 후속 사건을 우선한다. 만료가 같은 해인 후보끼리는 기존 시드 추첨을 쓴다.
  const followUps = eligible.filter((e) => e.trigger);
  const earliest = followUps.length ? Math.min(...followUps.map((e) => e.trigger.deadline)) : null;
  const fresh = eligible.filter((e) => !(s.seen ?? []).includes(e.id));
  const pool = followUps.length ? followUps.filter((e) => e.trigger.deadline === earliest)
    : fresh.length ? fresh : eligible;
  // 재등장 대기 중인 사건밖에 없으면 올해는 사건 없이 진행한다.
  if (!pool.length) return null;
  const w = pool.map((e) => eventWeightParts(s, e, activeFocus).total);
  const selected = (event) => ({ ...event, drawContext: { age: s.age, focus: activeFocus,
    weightParts: eventWeightParts(s, event, activeFocus) } });
  let r = yearRng(s, s.age, 2)() * w.reduce((a, b) => a + b, 0);
  for (let i = 0; i < pool.length; i++) { r -= w[i]; if (r <= 0) return selected(pool[i]); }
  return selected(pool[pool.length - 1]);
}

// ---------- 자동 결정: 기질(오행 비율)이 먼저, 능력치는 보조, 난수는 동점 깨기 ----------
const EL_NAMES = ['목', '화', '토', '금', '수'];
const TRAIT_WARMTH = { 배려: 0.5, 열정: 1, 안정: 0, 원칙: -0.5, 신중: -1 };
const pct = (v) => Math.round(v);

/** 자동 결정의 이유를 방금 남긴 기록과 결정 흔적에 붙인다. 상태가 바뀌지 않았으면(prev === next) 아무것도 하지 않는다. */
export function annotateAuto(prev, next, reason, match = null, { trace = true } = {}) {
  if (!next || next === prev || !reason) return next;
  const entry = (next.log ?? []).find(match ?? (() => true));
  if (entry) entry.autoReason = reason;
  const last = next.decisionTrace?.at(-1);
  if (trace && last) last.autoReason = reason;
  return next;
}

/** 행동 선택지 가산: 이벤트 분야의 원국 능력치가 다섯 분야 평균보다 높을수록 행동 쪽으로 기운다(원국 안의 상대 강도). */
function natalStrengthBonus(s, field) {
  const st = s.saju.stats ?? {}, values = GROUPS.map((g) => st[g] ?? 0);
  return AUTO_RULES.natalStrength * ((st[field] ?? 0) - values.reduce((a, b) => a + b, 0) / values.length);
}

/** 자동 진행용 이벤트 선택: 행동은 이벤트 분야의 오행(일간 기준) 비율(하한 있음)+원국 강도, 거절·대안은 고정 기준점에서 출발하고, 한난조습 기울기와 작은 난수를 더해 가장 높은 것 */
export function autoChoiceDetail(s, event, focus = null, focusUnits = focus ? 1 : 0) {
  const tilt = climateTilt(climateOf(s.saju), AUTO_CLIMATE_WEIGHT);
  const fieldEl = elementOfGroup(s, event.field), fieldPct = s.saju.elements[fieldEl] ?? 0;
  let best = 0, bestScore = -Infinity, info = null;
  event.choices.forEach((c, i) => {
    if (!timePlan(s, { focus, focusUnits, event, choiceIdx: i }).valid) return;
    const kind = c.kind ?? 'act', bias = tilt[kind] ?? 0;
    const strength = kind === 'act' ? natalStrengthBonus(s, event.field) : 0;
    const base = kind === 'act' ? Math.max(fieldPct, AUTO_RULES.actFloor) : AUTO_RULES.declineBase;
    const sc = base + bias + strength + yearRng(s, s.age, 3 + i)() * AUTO_RULES.choiceNoise;
    if (sc > bestScore) {
      bestScore = sc; best = i;
      info = { trait: kind === 'act' ? EL_TRAIT[fieldEl] : c.trait, base: kind === 'act' ? fieldPct : traitScore(s, c.trait), bias, strength };
    }
  });
  const reason = info && `기질 ${info.trait} ${pct(info.base)}%`
    + (Math.abs(info.strength) >= 1 ? ` · 원국 강도 ${info.strength > 0 ? '+' : ''}${pct(info.strength)}` : '')
    + (Math.abs(info.bias) >= 1 ? ` · 한난조습 ${info.bias > 0 ? '+' : ''}${info.bias.toFixed(1)}` : '');
  return { idx: best, reason };
}
export const autoChoice = (s, event, focus = null, focusUnits = focus ? 1 : 0) => autoChoiceDetail(s, event, focus, focusUnits).idx;

/** 일간 기준 그 분야가 속한 오행. 비겁=같은 오행, 식상=내가 생, 재성=내가 극, 관성=나를 극, 인성=나를 생 (십성은 일간마다 오행이 다르다) */
const elementOfGroup = (s, group) => ['목', '화', '토', '금', '수'][(STEM_EL[s.saju.day?.stem ?? 0] + GROUPS.indexOf(group)) % 5];

/** 자동 진행용 집중 분야: 열린 기회 중 그 분야의 오행(일간 기준)이 원국에서 가장 큰 영역(+능력치 보조) */
export function autoFocusPick(s) {
  const open = opensFor(s, s.age).open, pool = open.length ? open : GROUPS;
  const scored = pool.map((g) => {
    const el = elementOfGroup(s, g), base = s.saju.elements[el] ?? 0;
    return { g, el, base, score: base + AUTO_RULES.statAssist * (s.stats[g] ?? 0) };
  }).sort((a, b) => b.score - a.score);
  const p = scored[0];
  return { field: p.g, reason: `기질 ${EL_TRAIT[p.el]} ${pct(p.base)}% → ${GROUP_LABEL[p.g]} 집중${open.length ? ' (열린 기회 중)' : ''}` };
}
export const autoFocus = (s) => autoFocusPick(s).field;

/** 자동 진행용 첫 직업: 제안된 직업들 중 두 갈래의 기질 평균이 가장 높은 것(+능력치 보조) */
export function autoJob(s, list) {
  let best = null;
  for (const job of list ?? []) {
    const traits = (job.branches ?? []).map((b) => b.trait);
    const avg = traits.length ? traits.reduce((sum, t) => sum + traitScore(s, t), 0) / traits.length : 0;
    const fit = s.luckModel === 'environment' && Number.isFinite(job.fit) ? job.fit : 0;
    const score = avg + AUTO_RULES.statAssist * (s.stats[job.field] ?? 0) + AUTO_RULES.fitAssist * fit;
    if (!best || score > best.score) best = { job, score, avg, traits, fit };
  }
  return best && { job: best.job, reason: `기질 평균 ${pct(best.avg)}% (${[...new Set(best.traits)].join('·')})${best.fit ? ` · 방향 어울림 ${best.fit}/3` : ''} · ${best.job.name}` };
}

/** 자동 진행용 4단계 갈래: 기질 비율이 높은 쪽. 동점이면 한난(따뜻하면 열정·배려, 추우면 신중·원칙) */
export function autoBranch(s) {
  const order = branchOrder(s), top = order[0].score;
  const tied = order.filter((b) => b.score === top);
  const sign = Math.sign(climateOf(s.saju)?.warm.score ?? 0);
  const pick = [...tied].sort((a, b) => (TRAIT_WARMTH[b.trait] - TRAIT_WARMTH[a.trait]) * sign)[0];
  return { idx: pick.idx, reason: `기질 ${pick.trait} ${pct(pick.score)}%${tied.length > 1 ? ' · 동점이라 한난 기준' : ''}` };
}

/** 자동 진행용 관계 요청: 배려가 신중 이상이고 시간이 남으면 범위를 줄여 수락 */
export function autoRelationshipKind(s, available) {
  const care = traitScore(s, '배려'), cautious = traitScore(s, '신중');
  const accept = available >= 1 && care >= cautious;
  return { kind: accept ? 'limited' : 'decline',
    reason: `배려 ${pct(care)}% ${care >= cautious ? '≥' : '<'} 신중 ${pct(cautious)}%${available < 1 ? ' · 남은 시간 없음' : ''}` };
}

/** 그 해의 대운 상태를 기록용으로 줄인 값. 유형은 승인된 전환 유형 표가 연결된 뒤에만 채워진다. */
export function luckRecord(s, age) {
  const luck = luckAt(s.saju, age);
  if (!luck.daewoon) return null;
  const type = luck.inTransition && getTransitionTable() ? classifyTransition(essenceOf(s.saju), luck) : null;
  return { ten: luck.ten, group: luck.group, startAge: luck.startAge, yearsSince: luck.yearsSince, inTransition: luck.inTransition, type };
}

/** 새 형식 장면 추첨: 올해 나올 수 있는 장면이 있으면 장면마다 정한 확률(rate)로 나오고, 없으면 평범한 해다. */
export function drawScene(s, candidates) {
  if (s.age < JOB_RULES.eventStartAge || !candidates?.length) return null;
  const eligible = candidates.map((e) => prepareEvent(s, e, e.cooldownYears ?? 99)).filter(Boolean)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (!eligible.length) return null;
  const none = eligible.reduce((p, e) => p * (1 - (e.rate ?? 0.4)), 1);
  if (yearRng(s, s.age, 230)() >= 1 - none) return null;
  const total = eligible.reduce((sum, e) => sum + (e.rate ?? 0.4), 0);
  let r = yearRng(s, s.age, 231)() * total;
  for (const e of eligible) { r -= e.rate ?? 0.4; if (r <= 0) return { ...e, drawContext: { age: s.age } }; }
  return { ...eligible.at(-1), drawContext: { age: s.age } };
}

/** 이번 나이에 선택 이벤트로 올릴 인생 사건(없으면 null). 있으면 일반 사건 추첨 대신 이것을 쓴다(인생 사건 우선). */
export const lifeEvent = (s) => lifeEventFor(s, opensFor(s, s.age).open, prepareEvent);

/** 한 해를 마무리하고 다음 나이로 */
export function resolveYear(prev, { focus, event, choiceIdx, focusUnits = focus ? 2 : 0, participantId = null }) {
  const plan = timePlan(prev, { focus, focusUnits, event, choiceIdx });
  if (!plan.valid) throw new Error(plan.reason);
  const s = structuredClone(prev);
  const age = s.age, rng = yearRng(s, age, 1), op = opensFor(s, age);
  const acted = new Set(), before = { ...s.stats };
  const changes = [];
  // 인생 사건 판정은 난수가 나이별로 고정이라 화면이 이벤트를 만들 때와 같은 결과다. 상태 전이는 여기서 한 번만 반영한다.
  const lifeRoll = s.lifeModel === LIFE_MODEL && s.life && age >= JOB_RULES.eventStartAge ? rollLife(prev, age, op.open) : null;
  if (lifeRoll) s.life = lifeRoll.life;
  if (lifeRoll?.petDeaths) s.log.unshift({ age, kind: 'lifeEvent', lifeType: 'petDeath', auto: true, text: lifeRecordText('petDeath') }); // 반려동물이 수명을 다하면 기록으로만 남긴다
  const lifeHappened = []; // 그 해 일어난 인생 사건(경제 정산에 쓴다)
  s.seen ??= [];
  s.eventLastSeen ??= {};
  s.log ??= [];
  s.history ??= [];
  s.people ??= [];
  s.eventHistory = getEventHistory(s);
  s.decisionTrace ??= [];

  // 성장 효율은 양수 행동에만 적용한다. 운은 이 경로에서 한 번만 더한다.
  // 다른 기능에서 주는 고정 도움은 passive로 기록하며 운이나 행동 판정을 더하지 않는다.
  const applyAction = (field, baseDelta, kind, failed = false, passive = false, countsAsAction = true) => {
    const previous = s.stats[field];
    const growthFactor = baseDelta > 0 && !passive
      ? Math.max(BALANCE_RULES.growthFloor, (STAT_MAX - previous) / (STAT_MAX - STAT_MIN)) : 1;
    const action = baseDelta > 0 && !passive ? baseDelta * growthFactor * s.momentum[field] : baseDelta;
    const luck = s.luckModel !== 'environment' && action > 0 && !failed && !passive && op.open.includes(field)
      ? action * BALANCE_RULES.luckShare * Math.min(op.map[field], 10) / 10 : 0;
    const requestedDelta = action + luck;
    const after = roundStat(clamp(previous + requestedDelta, STAT_MIN, STAT_MAX));
    const appliedDelta = roundStat(after - previous);
    s.stats[field] = after;
    if (baseDelta !== 0 && !passive && countsAsAction) acted.add(field);
    const change = { kind, field, before: previous, after, baseDelta, growthFactor,
      momentum: s.momentum[field], action, luck, requestedDelta, appliedDelta,
      unappliedDelta: requestedDelta - appliedDelta, failed };
    changes.push(change);
    return change;
  };

  if (focus && plan.focusUnits > 0 && age >= JOB_RULES.eventStartAge) {
    const change = applyAction(focus, BALANCE_RULES.focusGain * plan.focusUnits / LIFE_RULES.fullFocusUnits, 'focus');
    s.log.unshift({ age, kind: 'focus', field: focus, delta: change.appliedDelta,
      text: `${focus} 분야에 시간 ${plan.focusUnits}칸을 썼다`, result: `${focus} ${signed(change.appliedDelta)}` });
  }
  if (s.yearCommitment?.age === age) {
    for (const action of s.yearCommitment.actions ?? []) {
      const change = applyAction(action.field, action.baseDelta, 'relationship');
      s.log.unshift({ age, kind: 'people', field: action.field, delta: change.appliedDelta,
        text: action.solo ? '혼자 내 일에 시간을 썼다' : `${action.personName}와 함께한 일`, result: `${GROUP_LABEL[action.field] ?? action.field} ${signed(change.appliedDelta)}` });
    }
  }
  if (event && choiceIdx != null) {
    const c = event.choices[choiceIdx], t = c.targetField;
    if (!s.seen.includes(event.id)) s.seen.push(event.id);
    s.eventLastSeen[event.id] = age;
    const choiceKind = c.kind ?? 'act';
    const failed = choiceKind !== 'decline' && rng() < c.risk;
    const outcome = choiceKind === 'decline' ? 'skipped' : failed ? 'failure' : 'success';
    const effects = choiceEffects(c, failed), applied = {};
    for (const [field, delta] of Object.entries(effects)) {
      if (!GROUPS.includes(field) || !Number.isFinite(delta) || delta === 0) continue;
      // 지출·부담으로 내려간 분야를 그 분야의 연습으로 인정하지 않는다.
      const change = applyAction(field, delta, 'event', failed, false, delta > 0);
      applied[field] = change.appliedDelta;
    }
    // 시도했지만 실패한 해도 해당 분야에서 행동한 해다. 하지 않기는 행동으로 세지 않는다.
    if (choiceKind !== 'decline' && GROUPS.includes(t)) acted.add(t);
    const parentKey = event.trigger?.key ?? null;
    const baseNarrative = (failed ? c.failureText : c.successText)
      || (choiceKind === 'decline' ? '이번 제안에는 참여하지 않았다.' : failed ? '뜻대로 되지 않았다.' : '선택한 일을 마쳤다.');
    const companion = choiceKind !== 'decline' ? s.people.find((person) => person.id === participantId
      && personAvailability(s, person).available) : null;
    const shared = companion ? `${companion.name}와 이 일을 함께 검토했다.` : '';
    const narrative = shared ? `${baseNarrative} ${shared}` : baseNarrative;
    const result = Object.entries(applied).map(([field, delta]) => `${field} ${signed(delta)}`).join(' · ')
      || '능력치 변화 없음';
    s.eventHistory.push({ eventId: event.id, age, choiceIdx, choiceKind, outcome, parentKey,
      contentVersion: event.contentVersion ?? 0, effects: applied, field: t, eventField: event.field,
      title: event.title, choiceLabel: c.label, narrative, participantId: companion?.id ?? null,
      preparationBefore: preparationFor(prev, t), natalApproach: event.approach?.kind ?? null, timePlan: plan });
    if (companion) {
      companion.trust = clamp((companion.trust ?? 50) + (failed ? 1 : 2), 0, 100);
      addMemory(companion, { age, kind: 'shared', title: event.title, outcome,
        text: `${c.label} · ${failed ? '시도를 마치지 못했지만 함께 확인했다.' : '함께 맡은 일을 마쳤다.'}` });
    }
    s.log.unshift({ age, kind: 'event', eventId: event.id, choiceIdx, choiceKind, outcome, parentKey,
      contentVersion: event.contentVersion ?? 0,
      field: t, delta: applied[t] ?? 0, effects: applied, narrative,
      weightParts: event.drawContext?.weightParts ?? eventWeightParts(prev, event, plan.focusField),
      failed, text: `${event.title} → ${c.label}`,
      result: `${result} (${OUTCOME_LABEL[outcome]})` });
    s.decisionTrace.push({ age, eventId: event.id, choiceIdx, choiceKind, participantId: companion?.id ?? null,
      focus: plan.focusField, focusUnits: plan.focusUnits, outcome });
    if ((event.category === 'scene' || event.category === 'life') && s.life) { applySceneMoney(s, event, c, failed, age); applySceneLife(s, c, failed); }
    if (event.lifeType && lifeRoll) s.life.lastOffer = { ...(s.life.lastOffer ?? {}), [event.lifeType]: age };
    if (event.lifeType && lifeRoll) s.life.offerCount = { ...(s.life.offerCount ?? {}), [event.lifeType]: (s.life.offerCount?.[event.lifeType] ?? 0) + 1 };
    // 직업 전환 사건: 새 일을 찾는 선택이 성공하면 다음 단계에서 실제 직업 후보가 나온다(행동 = 같은 분야, 다른 방법 = 다른 분야)
    if (event.lifeType === 'jobChange' && !failed && choiceKind !== 'decline' && s.job) s.pendingJobSwitch = choiceKind === 'act' ? 'same' : 'other';
    if (event.lifeType === 'pet' && lifeRoll) s.life.pet.offers = (s.life.pet.offers ?? 0) + 1;
    if (event.lifeType && lifeRoll && choiceKind === 'act' && !failed) {
      applyLifeAct(s.life, event.lifeType, age, yearRng(s, age, 225)());
      lifeHappened.push(event.lifeType);
      s.log.unshift({ age, kind: 'lifeEvent', lifeType: event.lifeType, text: lifeRecordText(event.lifeType) });
    }
  }
  // 선택 없이 자동 기록으로 처리하는 두 번째 인생 사건
  if (lifeRoll?.record) {
    applyLifeAct(s.life, lifeRoll.record.type, age, yearRng(s, age, 225)());
    lifeHappened.push(lifeRoll.record.type);
    s.log.unshift({ age, kind: 'lifeEvent', lifeType: lifeRoll.record.type, auto: true, text: lifeRecordText(lifeRoll.record.type) });
  }
  if (age < 15 || (!event && !plan.focusUnits && !plan.relationshipUnits)) {
    s.log.unshift({ age, kind: 'life', text: age < 15 ? lifePhase(age).record : '추가 활동을 선택하지 않고 한 해를 보냈다.' });
  }
  // 기운: 행동한 분야는 쌓이고, 안 한 분야는 매년 10% 식는다
  for (const g of GROUPS) s.momentum[g] = acted.has(g) ? Math.min(1.5, s.momentum[g] + 0.1) : 1 + (s.momentum[g] - 1) * 0.9;

  // 승진: 요구치 + 그 해 해당 분야에서 행동 + 확률
  const j = s.job;
  if (j && age < JOB_RULES.retireAge && j.level < 4 && j.since !== age) { // 전직한 해에는 승진 판정을 하지 않는다
    const req = JOB_RULES.levels[j.level + 1];
    if (s.stats[j.field] >= req.main && s.stats[j.sub] >= req.sub && age >= req.minAge && acted.has(j.field)) {
      const p = Math.min(0.85, 0.35 + 0.15 * (s.momentum[j.field] - 1) / 0.5);
      if (rng() < p) {
        j.level += 1;
        if (j.level === 4) s.branchPending = true;
        s.log.unshift({ age, kind: 'job', text: `승진했다 → ${jobTitle(j)}` });
      }
    }
  }
  // 관계 자리는 고정. 비겁은 행동한 해의 친밀도 증가만 보정하고 도움 확률·보상에는 중복 가산하지 않는다.
  const relationFactor = relationshipGrowthFactor(s);
  s.people.filter((q) => age >= 15 && q.status === 'near').forEach((q, i) => {
    const sharedThisYear = q.memories?.some((memory) => memory.age === age && memory.kind === 'shared');
    const gain = acted.has(q.group) || sharedThisYear ? (3 + (q.compat >= 4 ? 1 : 0)) * relationFactor : -1;
    q.closeness = roundStat(clamp(q.closeness + gain, 0, 100));
    if (q.closeness >= HELP_CLOSENESS && yearRng(s, age, 40 + i)() < 0.3) {
      const change = applyAction(q.group, 1, 'people', false, true);
      q.impact = roundStat((q.impact ?? 0) + change.appliedDelta);
      q.notes ??= [];
      q.notes.unshift({ age, text: `${q.group} ${GROUP_LABEL[q.group]}에 도움을 줬다 (${signed(change.appliedDelta)})` });
      q.notes.length = Math.min(q.notes.length, 6);
      s.log.unshift({ age, kind: 'people', field: q.group, delta: change.appliedDelta,
        text: `${q.name}의 도움`, result: `${q.group} ${signed(change.appliedDelta)}` });
    }
  });
  // 소득·자산 정산(가구 단위). 새 진행에만 있다.
  if (lifeRoll) s.life.economy = stepEconomy(s, age, lifeHappened);
  // 대운이 바뀌는 해에는 장면 문장을 진행 기록에 한 줄 남긴다(문장 표가 연결되어 있을 때만. 진행 결과에는 영향이 없다).
  const scene = luckScene(s);
  if (scene && scene.yearsSince === 0) s.log.unshift({ age, kind: 'luck', text: scene.body });
  // 옛 저장(운 가산을 쓰는 진행)은 자기 규칙 버전을 유지한다. 새 규칙으로 덮어쓰면 같은 규칙으로 기록한 것처럼 보인다.
  const environment = s.luckModel === 'environment';
  const balanceVersion = environment ? BALANCE_RULES.version : (s.balanceVersion ?? 5);
  const rulesVersion = environment ? LIFE_RULES.version : (s.engineRulesVersion ?? 5);
  s.balanceVersion = balanceVersion;
  s.engineRulesVersion = rulesVersion;
  s.history.push({ age, stats: { ...s.stats }, before, opens: op.map, acted: [...acted], job: jobTitle(j),
    balanceVersion, engineRulesVersion: rulesVersion, changes, timePlan: plan, luck: luckRecord(s, age),
    phase: lifePhase(age).id });
  s.yearCommitment = null;
  s.age += 1;
  if (s.age > s.deathAge) s.status = 'ended';
  return s;
}

// ───────── 곁의 사람 (NPC) ─────────
export const MAX_PEOPLE = 5;
export const relationshipAvailability = personAvailability;
// 낮은 관계 능력치도 관계 행동의 효과를 없애지 않는다. 5~100에서 0.75~1.25배.
export const relationshipGrowthFactor = (s) => BALANCE_RULES.relationshipMin
  + BALANCE_RULES.relationshipRange * (clamp(s.stats.비겁 ?? STAT_MIN, STAT_MIN, STAT_MAX) - STAT_MIN) / (STAT_MAX - STAT_MIN);
const HELP_CLOSENESS = 60;
const SURNAMES = ['김', '이', '박', '최', '정', '강', '조', '윤', '장', '임', '한', '오', '서', '신', '권', '황'];
// 일간 기준 그 사람의 십성 그룹 → 역할 (비겁은 친구/라이벌 중 하나)
const ROLE = { 인성: ['멘토'], 비겁: ['친구', '라이벌'], 관성: ['동료'], 재성: ['연인'], 식상: ['후배'] };
const HINT = { 멘토: '나를 이끌어 줄 사람', 친구: '곁에서 함께해 줄 사람', 라이벌: '경쟁하며 자극을 줄 사람', 동료: '같이 일을 풀어 갈 사람', 연인: '마음을 나눌 사람', 후배: '내가 이끌어 줄 사람' };
// 상성(1~5): 나를 생해 주면 5, 내가 생해 주면 4, 같은 오행·내가 극하면 3, 나를 극하면 2. 라이벌은 -1
const COMPAT = { 인성: 5, 식상: 4, 비겁: 3, 재성: 3, 관성: 2 };
const RELATION_TEXT = { 인성: '나를 생해 주는 사이', 식상: '내가 생해 주는 사이', 비겁: '같은 기운을 나누는 사이', 재성: '내가 다스리는 기운의 사이', 관성: '나를 다듬어 주는 사이' };
export const personElement = (q) => ['목', '화', '토', '금', '수'][STEM_EL[q.stem]];
export const personTrait = (q) => EL_TRAIT[personElement(q)];
export const personRelationText = (q) => `${RELATION_TEXT[q.group]} · ${q.group} ${GROUP_LABEL[q.group]} 영역에 도움`;
export const nearPeople = (s) => (s.people ?? []).filter((q) => q.status === 'near');

function makePerson(s, rng, usedNames) {
  const cycle = Math.floor(rng() * 60), stem = cycle % 10, branch = cycle % 12;
  const group = groupOfStem(s.saju.day.stem, stem);
  const roles = ROLE[group], role = roles[Math.floor(rng() * roles.length)];
  let name;
  do { name = `${SURNAMES[Math.floor(rng() * SURNAMES.length)]}OO`; } while (usedNames.has(name) && usedNames.size < SURNAMES.length);
  usedNames.add(name);
  return {
    id: `p${s.age}-${Math.floor(rng() * 1e6)}`, name, role, group, stem, branch,
    compat: Math.max(1, COMPAT[group] - (role === '라이벌' ? 1 : 0)),
    hint: HINT[role], ageDiff: Math.floor(rng() * 9) - 4,
    closeness: 30, sinceAge: s.age, driftAge: null, status: 'near', impact: 0, notes: [],
    trust: 50, memories: [], promise: null,
  };
}

// 어릴 때 친구(게임 추정): 8세부터 알던 같은 기운의 친구 2명. 15세 이전에는 관계 행동이 없으므로 친밀도는 15세부터 쌓인다.
function childhoodFriends(run) {
  const rng = mulberry32((run.seed ^ 0x5bd1e995) >>> 0), friends = [], names = new Set();
  for (let i = 0; i < 2; i++) {
    for (let tries = 0; tries < 80; tries++) {
      const p = makePerson({ ...run, age: 8 }, rng, new Set(names));
      if (p.group === '비겁' && p.role === '친구') { names.add(p.name); friends.push({ ...p, closeness: 40, id: `c${i}-${p.id}` }); break; }
    }
  }
  return friends;
}

// 만남 확률(게임 추정): 어릴수록 새 친구를 사귀기 쉽다.
const meetRate = (age) => (age < 20 ? 0.5 : age < 30 ? 0.3 : 0.16);

/** 올해 새 만남이 있으면 후보 2명, 없으면 null */
export function meetOffer(s) {
  if (s.meetAge === s.age || s.age < 15 || s.age > 65) return null;
  const rng = yearRng(s, s.age, 21);
  if (rng() >= meetRate(s.age)) return null;
  const used = new Set(s.people.map((q) => q.name));
  return [makePerson(s, rng, used), makePerson(s, rng, used)];
}

/** 만남 결과. pick: 후보 인덱스(없으면 null), dropId: 자리가 없을 때 멀어질 사람 */
export function resolveMeet(prev, { offer, pick, dropId, alone }) {
  if (pick != null) {
    const near = nearPeople(prev);
    if (!offer?.[pick] || near.some((q) => q.id === offer[pick].id)) return prev;
    // 화면뿐 아니라 엔진에서도 고정 자리 수를 지킨다.
    if (near.length >= MAX_PEOPLE && !near.some((q) => q.id === dropId)) return prev;
  }
  const s = structuredClone(prev);
  s.meetAge = s.age;
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'meet', personId: pick == null ? null : offer[pick].id,
    pick, dropId: dropId ?? null });
  if (pick == null) {
    s.log.unshift({ age: s.age, kind: 'people', text: alone ? '혼자 일에 집중했다' : '새로 만난 사람들과 적당히 지냈다' });
    return s;
  }
  if (dropId) driftIn(s, dropId);
  const q = offer[pick];
  s.people.push(q);
  s.log.unshift({ age: s.age, kind: 'people', text: `${q.name}(${q.role})와 가까워졌다` });
  return s;
}
function driftIn(s, id) {
  const q = s.people.find((x) => x.id === id);
  if (q) {
    q.status = 'drifted'; q.driftAge = s.age;
    if (q.promise?.status === 'pending') {
      q.promise.status = 'cancelled';
      addMemory(q, { age: s.age, kind: 'promise', title: '관계 거리 조정', outcome: 'cancelled', text: '관계의 거리를 조정하며 예정한 공동 활동도 종료했다.' });
    }
    s.log.unshift({ age: s.age, kind: 'people', text: `${q.name}(${q.role})와 거리를 뒀다` });
  }
}
export function driftPerson(prev, id) {
  const s = structuredClone(prev); driftIn(s, id);
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'drift', personId: id });
  return s;
}

const topElements = (els) => { const max = Math.max(...EL_NAMES.map((e) => els[e] ?? 0)); return EL_NAMES.filter((e) => (els[e] ?? 0) === max); };
const relationOf = (a, b) => {
  const d = (EL_NAMES.indexOf(b) - EL_NAMES.indexOf(a) + 5) % 5;
  return d === 0 ? 'same' : d === 1 || d === 4 ? 'sheng' : 'ke';
};
const RELATION_LABEL = { same: '같은 기운', sheng: '서로 생', ke: '서로 극' };

/** 자동 진행: 상성에 내 기질 오행과 상대 오행의 생극을 더해 높은 쪽과 가까워진다. 자리가 없으면 가장 덜 가까운 사람과 멀어진다 */
export function autoMeet(s, offer) {
  const mine = topElements(s.saju.elements);
  const scoreOf = (q) => {
    const rels = mine.map((el) => relationOf(el, personElement(q)));
    const bonus = rels.reduce((sum, r) => sum + AUTO_RULES.relation[r], 0) / rels.length;
    return { q, rel: rels[0], score: q.compat + bonus };
  };
  const a = scoreOf(offer[0]), b = scoreOf(offer[1]);
  const pick = a.score >= b.score ? 0 : 1, chosen = pick ? b : a;
  let dropId;
  const near = nearPeople(s);
  if (near.length >= MAX_PEOPLE) dropId = [...near].sort((x, y) => x.closeness - y.closeness)[0].id;
  const next = resolveMeet(s, { offer, pick, dropId });
  return annotateAuto(s, next, `상성 ${chosen.q.compat} · 상대 ${personElement(chosen.q)}(${RELATION_LABEL[chosen.rel]}) → ${chosen.score.toFixed(1)}점`,
    (l) => l.kind === 'people');
}

function addMemory(person, memory) {
  person.memories ??= [];
  person.memories.unshift(memory);
}

// 곁의 사람이 하자고 하는 일을 나이에 맞는 구체적인 활동으로 쓴다(같은 사람이 73세에도 "학업 활동"을 하자고 하지 않게).
const REQUEST_PHRASE = {
  비겁: ['친구들과 모임에 같이 가자', '친구들과 여행을 같이 가자', '가까운 사람들과 모임을 같이 준비하자', '오랜 지인들과 모임을 같이 나가자', '동네 모임에 같이 나가자'],
  식상: ['취미 동아리를 같이 해 보자', '같이 취미 활동을 시작하자', '주말에 같이 운동하자', '같이 취미 모임에 나가 보자', '같이 산책과 가벼운 운동을 하자'],
  재물: ['용돈 쓰는 방법을 같이 정리하자', '같이 저축 계획을 세우자', '집안 지출을 같이 점검하자', '노후 자금을 같이 점검하자', '생활비 쓰임을 같이 정리하자'],
  관성: ['아르바이트 자리를 같이 알아보자', '일하는 방식을 같이 정리하자', '직장 고민을 같이 상의하자', '앞으로의 일을 같이 정리하자', '경험을 살려 같이 할 일을 찾아보자'],
  인성: ['같이 공부하자', '같이 자격증 공부를 하자', '같이 온라인 강의를 듣자', '같이 책 읽는 모임을 하자', '문화센터 강좌를 같이 듣자'],
};
// 영역 값을 모르는 옛 기록의 사람에게도 오류 없이 일반 문구를 쓴다.
const requestPhrase = (field, age) => REQUEST_PHRASE[field]?.[age < 20 ? 0 : age < 30 ? 1 : age < 50 ? 2 : age < 60 ? 3 : 4] ?? '함께할 일을 같이 해 보자';

export function relationshipOffer(s) {
  if (s.age < 15 || s.relationshipAge === s.age) return null;
  const people = nearPeople(s);
  const due = people.find((person) => person.promise?.status === 'pending' && person.promise.dueAge <= s.age);
  const available = people.filter((person) => personAvailability(s, person).available);
  if (!due && (!available.length || yearRng(s, s.age, 71)() >= .25)) return null;
  const person = due ?? available[Math.floor(yearRng(s, s.age, 72)() * available.length)];
  const field = person.promise?.status === 'pending' ? person.promise.field : person.group;
  const lastShared = person.memories?.find((memory) => memory.kind === 'shared'
    || (memory.kind === 'relationship' && ['accepted', 'kept'].includes(memory.outcome)));
  const lastDecision = person.memories?.[0];
  const context = lastShared ? `${lastShared.age}세에 함께했던 일을 바탕으로 이번에 맡을 범위를 정합니다.`
    : lastDecision ? `${lastDecision.age}세의 기록: ${lastDecision.text} 이번 제안의 범위는 다시 정할 수 있습니다.`
      : '처음 함께 맡을 일의 범위와 시간을 정합니다.';
  return { personId: person.id, personName: person.name, field, duePromise: !!due, ownField: autoFocusPick(s).field,
    title: due ? `${person.name}와 약속한 일을 확인한다` : `${person.name}가 ${requestPhrase(field, s.age)}고 한다`,
    description: due ? `${person.promise.madeAge}세에 다음 해로 약속했던 일입니다. 함께할 범위를 줄이거나 이번에 지키기 어렵다고 말할 수 있습니다.` : context };
}

export function resolveRelationship(prev, { offer, kind, focusField = null, focusUnits = focusField ? 2 : 0 }) {
  const current = relationshipOffer(prev);
  if (!current || current.personId !== offer?.personId || !['help', 'limited', 'decline', 'promise'].includes(kind)) return prev;
  if (current.duePromise && kind === 'promise') return prev;
  const cost = kind === 'help' ? LIFE_RULES.helpUnits : kind === 'limited' ? LIFE_RULES.limitedHelpUnits : 0;
  const plan = timePlan(prev, { focus: focusField, focusUnits });
  if (plan.used + cost > plan.capacity) return prev;
  const s = structuredClone(prev), person = s.people.find((entry) => entry.id === current.personId);
  if (cost && !personAvailability(s, person).available) return prev;
  person.trust ??= 50;
  const trustDelta = kind === 'help' ? 6 : kind === 'limited' ? 3 : current.duePromise && kind === 'decline' ? -4 : 0;
  person.trust = clamp(person.trust + trustDelta, 0, 100);
  let text, outcome;
  if (kind === 'promise') {
    person.promise = { madeAge: s.age, dueAge: s.age + 1, field: current.field, status: 'pending' };
    text = `${person.name}와 다음 해에 ${GROUP_LABEL[current.field]} 활동을 하기로 약속했다.`;
    outcome = 'promised';
  } else if (kind === 'decline') {
    if (current.duePromise) person.promise.status = 'broken';
    text = current.duePromise ? `${person.name}에게 약속한 일을 이번에 지키기 어렵다고 말했다.` : `${person.name}의 이번 제안을 거절하고 일정을 유지했다.`;
    outcome = current.duePromise ? 'broken' : 'declined';
  } else {
    if (current.duePromise) person.promise.status = 'kept';
    text = `${person.name}와 ${kind === 'limited' ? '범위를 줄여' : '시간을 내서'} 함께하기로 했다.`;
    outcome = current.duePromise ? 'kept' : 'accepted';
  }
  addMemory(person, { age: s.age, kind: 'relationship', title: current.title, text, outcome });
  s.relationshipAge = s.age;
  // 함께하면 그 사람의 영역이 오르고 신뢰가 쌓인다. 범위를 줄이면 절반씩, 거절하면 그 시간에 내 일을 해서 내 주력 영역이 오른다(함께하기만 이득이 되지 않게).
  const own = current.ownField ?? autoFocusPick(prev).field, base = { personId: person.id, personName: person.name };
  const actions = kind === 'help' ? [{ ...base, field: current.field, baseDelta: 1 }]
    : kind === 'limited' ? [{ ...base, field: current.field, baseDelta: .5 }, { ...base, field: own, baseDelta: .5, solo: true }]
      : kind === 'decline' && !current.duePromise ? [{ ...base, field: own, baseDelta: 1, solo: true }] : [];
  s.yearCommitment = { age: s.age, units: cost, actions };
  s.log.unshift({ age: s.age, kind: 'people', text, personId: person.id,
    result: `신뢰 ${signed(trustDelta)}` });
  s.decisionTrace ??= [];
  s.decisionTrace.push({ age: s.age, kind: 'relationship', personId: person.id, choiceKind: kind,
    focus: focusField, focusUnits, timeCost: cost, outcome });
  return s;
}

/** 결과 화면용: 가장 오래 함께한 사람, 가장 큰 도움을 준 사람 */
export function peopleHighlights(s) {
  const end = s.age - 1;
  const span = (q) => (q.driftAge ?? end) - q.sinceAge;
  const all = s.people;
  const longest = [...all].sort((a, b) => span(b) - span(a))[0] ?? null;
  const biggest = [...all].filter((q) => q.impact > 0).sort((a, b) => b.impact - a.impact)[0] ?? null;
  return { longest, longestSpan: longest ? span(longest) : 0, biggest };
}
